import { titleCase } from "utils/common";

/**
 * General Validator Functions
 */
export function valueExists(value){
	return value && typeof(value) !== "undefined";
}

export function selectableValue(value){
	/*
		ReduxControls.Select calls validators with `value` in the format: { value, label },
		but many of these validators take values directly (when used in reducers).

		i.e. { value: "myValue", label: "labelForMyValueInSelectBox" } vs. just "myValue"

		This function just returns `value.value` if it exists, or else it falls back to the raw `value` argument.
	 */
	return (value && value.hasOwnProperty("value")) ? value.value : value;
}

export function isEmail(value){
	/**
	 * Regex from:
	 * 		http://emailregex.com/
	 * 		The regex that the API uses is difficult to convert to JavaScript
	 */
	var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	return re.test(value);
}

export function overLimit(value, limit){
	return value.length > limit;
}

export function overMax(value, max){
	return value > max;
}

export function validSelection(value){
	return !!(value.value && value.label);
}

export function required(value){
	if(value === undefined || value === ""){
		return `Field is required`;
	}
	return undefined;
}

export function requiredNotNull(value){
	if((value === undefined) || (value === "") || (value === null)){
		return "Field is required";
	}
	return undefined;
}

export function maxLength(max){
	/*
		This returns a validation function that can be assigned to a const, and then used
		in a `validate` value for redux-form.

		Don't try to call the function inline, as it will change value each render and
		cause an infinite rerender loop.

		e.g. Do this:
			const nameMaxLength = maxLength(50);

			<Field
				validate = {[nameMaxLength]}
			/>

		Don't do this:
			<Field
				validate = {[maxLength(50)]}
			/>
	*/

	return (value) => {
		let selectVal = selectableValue(value);
		if(selectVal?.length > max){
			return `Field can't be more than ${max} characters long.`;
		}
	};
}

export function isURL(value){
	if(!value){
		// We don't want this to overlap with `required`, so we accept empty values
		return undefined;
	}
	if(/^(https?):\/\/[-a-zA-Z0-9+&@#/%?=~_|!:,.;]*[-a-zA-Z0-9+&@#/%=~_|]/.test(value) === false){
		return "Field must be a valid URL with http/https protocol";
	}
	return undefined;
}

export function isSecureURL(value) {
	const hasHttpsPrefix = /^https:\/\//.test(value);
	const urlError = isProtocolOptionalURL(value);
	if(!value || (hasHttpsPrefix && !urlError)){
		return;
	}
	return "Field must be a valid URL with https protocol";
}

export function isProtocolOptionalURL(value){
	if(!value){
		// We don't want this to overlap with `required`, so we accept empty values
		return undefined;
	}

	const internal = "[-a-zA-Z0-9+&@#/%?=~_|!:,.;]";
	const terminal = "[-a-zA-Z0-9+&@#/%=~_|]";
	const pattern = new RegExp(`^((https?):\\/\\/)?${internal}+\\.${internal}*${terminal}$`);

	if(pattern.test(value) === false){
		/*
			This is a very similar pattern as used in isURL. Here, however, the https is optional and
			the url must contain a dot at some point. Lastly, there is an end of input boundary assertion,
			which will prevent things like URLs with spaces or special characters.

			To get those kinds of URLs to pass, they'll need to be URL encoded, e.g. %20 for a space.
		*/
		return "Field must be a valid URL";
	}
	return undefined;
}

/*
	These filename consts are tightly coupled, and should be relocated as a unit if we want to
	move them for reuse more broadly.
*/
const FILENAME_PATTERN = /^[-\A-Za-z0-9._~]+$/;
const FILENAME_ERROR_MESSAGE = "Filenames must only contain the following characters: Alphanumeric . - _ ~";
export function validFilenameCharacters(value){
	if(!FILENAME_PATTERN.test(value)){
		return FILENAME_ERROR_MESSAGE;
	}
	return undefined;
}

const VIDEO_NAME_PATTERN = /^[a-zA-Z0-9_\-+|:.~]+$/;
const VIDEO_NAME_ERROR_MESSAGE = "Video names must only contain Alphanumeric or these special chars: . _ - + | : ~";
// VIDEO_NAME_PATTERN plus [ ] ' " ? × } ` < { > , & ! $, used when SUPPORT_LEGACY_INNOVID_SPECIAL_CHARS is enabled
const VIDEO_NAME_PATTERN_LEGACY_INNOVID_CHARS = /^[a-zA-Z0-9_\-+|:.~[\]'"?×}`<{>,&!$]+$/;
const VIDEO_NAME_ERROR_MESSAGE_LEGACY_INNOVID_CHARS = "Video names must only contain Alphanumeric or these special chars: . _ - + | : ~ [ ] ' \" ? × } ` < { > , & ! $";

function videoNameCharactersValidator(pattern, errorMessage){
	return function(value){
		if(!pattern.test(value)){
			return errorMessage;
		}
	};
}

export const validVideoNameCharacters = videoNameCharactersValidator(VIDEO_NAME_PATTERN, VIDEO_NAME_ERROR_MESSAGE);
export const validVideoNameCharactersLegacyInnovid = videoNameCharactersValidator(
	VIDEO_NAME_PATTERN_LEGACY_INNOVID_CHARS,
	VIDEO_NAME_ERROR_MESSAGE_LEGACY_INNOVID_CHARS
);

const VIDEO_NAME_MAX_LENGTH = 185;
export const videoNameMaxLength = maxLength(VIDEO_NAME_MAX_LENGTH);

export function validNativeName(value){
	if(!FILENAME_PATTERN.test(value)){
		return "Creative Name must only contain the following characters: Alphanumeric . - _ ~";
	}
	return undefined;
}

export function inRange({ min, max }){
	return (value) => {
		// inRange tests are inclusive of bounds.
		if(value !== undefined && isNaN(Number(value))){
			return `Field must be a number`;
		}

		if(value < min){
			return `Field can't be less than ${min}.`;
		}
		if(value > max){
			return `Field can't be greater than ${max}.`;
		}
		return undefined;
	};
}

export function isInteger(value){
	let num = Number(value);
	if(value !== undefined && !Number.isInteger(num)){
		return "Field must be an integer.";
	}
	return undefined;
}


export function minValue(value, allValues, props, name){
	if(value < 1) {return "Field must a positive integer.";}
	return undefined;
}


export function maxOptions(maxOptions, name="options") {
	const message = `${titleCase(name)} - Exceeds maximum of ${maxOptions} ${name}`;

	return (options) => {
		if (options?.length > maxOptions) {
			return message;
		}

		return undefined;
	};
}

export function emailValidator(value) {
	if (!value) {
		// We don't want this to overlap with `required`, so we accept empty values
		return undefined;
	}

	const email = selectableValue(value);
	if (!isEmail(email)) {
		return `"${email}" is not a valid email address`;
	}

	return undefined;
}

export function emailArray(values) {
	if (!values || values.length === 0) {
		return undefined;
	}

	const invalidEmail = values
		.map(value => selectableValue(value))
		.find(email => email && !isEmail(email));

	return invalidEmail && `"${invalidEmail}" is not a valid email address`;
}

export function requiredArray(values) {
	if (!values || values.length === 0) {
		return "At least one required";
	}

	return undefined;
}
